import * as THREE from 'three';
import { Cutout } from './Cutout';
import type { Colliders } from './Colliders';
import type { Fetch } from './Fetch';

/**
 * A person who lives here: a standing photo cutout that walks a route across the
 * grass South Park style — the card slides along with a bob and a lean — keeps out
 * of trees and houses, pauses now and then, and topples with a thud when a thrown
 * ball or stick hits it, lies there a moment, then picks itself up. A person either
 * loops a route forever (`Ed`) or is hidden until `run()` sends them along a route
 * once — a visitor who comes out now and then (`Zach`).
 */
const _v = new THREE.Vector3();
const _probe = new THREE.Vector3();
const _still = new THREE.Vector3();

export class Person {
  readonly cutout: Cutout;
  readonly root: THREE.Group;
  speed = 1.1;
  /** a toy got them: name, toy kind, toy speed */
  onHit?: (name: string, kind: string, speed: number) => void;
  /** a one-shot run reached its end (they've gone) */
  onDone?: () => void;
  /** walking their route (false while hidden between visits) */
  active = true;
  private oneShot = false;
  private next = 1;
  private pause = 0;
  private stride = 0;
  /** 0 upright .. 1 flat on the ground */
  private tip = 0;
  private tipVel = 0;
  private downUntil = -1;
  private t = 0;
  private slideSide = 1;
  private lastHit = -1e9;

  constructor(
    readonly name: string,
    url: string,
    private height: number,
    private route: THREE.Vector3[],
    private colliders: Colliders,
  ) {
    this.cutout = new Cutout(url, height, 'camera', `person:${name}`);
    this.root = this.cutout.root;
    this.root.rotation.order = 'YXZ';
    this.root.position.copy(route[0]);
  }

  get down(): boolean {
    return this.tip > 0.5;
  }

  /** disappear until the next `run()` */
  hide(): void {
    this.active = false;
    this.root.visible = false;
  }

  /** appear at the start of `route` and follow it once at `speed`, then vanish */
  run(route: THREE.Vector3[], speed: number): void {
    this.route = route;
    this.next = 1;
    this.speed = speed;
    this.oneShot = true;
    this.pause = 0;
    this.tip = 0;
    this.tipVel = 0;
    this.downUntil = -1;
    this.root.position.copy(route[0]);
    this.root.visible = true;
    this.active = true;
  }

  update(dt: number, cameraPos: THREE.Vector3, fetch: Fetch | null): void {
    if (!this.active) return;
    this.t += dt;
    let moving = false;
    // --- the route: walk to the next point, pause there, go on
    if (!this.down && this.pause <= 0 && this.route.length > 1) {
      const to = this.route[this.next];
      _v.subVectors(to, this.root.position).setY(0);
      const d = _v.length();
      if (d < 0.3) {
        if (this.oneShot && this.next === this.route.length - 1) {
          this.hide();
          this.onDone?.();
          return;
        }
        this.next = (this.next + 1) % this.route.length;
        if (!this.oneShot) this.pause = 1 + Math.random() * 4; // a walker dawdles; a runner doesn't
      } else {
        moving = true;
        _v.multiplyScalar(1 / d);
        this.root.position.addScaledVector(_v, Math.min(d, this.speed * dt));
        // trunks and houses push back (chest height, so boxes push sideways); head-on, slide round
        _probe.set(this.root.position.x, 0.6, this.root.position.z);
        _still.set(0, 0, 0);
        this.colliders.resolve(_probe, _still, 0.3, 0);
        const px = _probe.x - this.root.position.x;
        const pz = _probe.z - this.root.position.z;
        this.root.position.x = _probe.x;
        this.root.position.z = _probe.z;
        const back = -(px * _v.x + pz * _v.z);
        if (back > 1e-4) {
          const lateral = px * _v.z - pz * _v.x;
          if (Math.abs(lateral) > 1e-4) this.slideSide = Math.sign(lateral);
          this.root.position.x += _v.z * back * this.slideSide;
          this.root.position.z -= _v.x * back * this.slideSide;
        }
        this.stride += dt * Math.PI * 2 * (1.9 * Math.min(2, this.speed / 1.1)); // feet go faster at a run
      }
    } else if (this.pause > 0) this.pause -= dt;

    // --- a toy: fast, close, and no higher than the head → over they go, and it bounces off
    if (fetch && !fetch.inHand && !fetch.carried && this.tip < 0.05 && this.t - this.lastHit > 1) {
      const toy = fetch.root.position;
      const dx = toy.x - this.root.position.x;
      const dz = toy.z - this.root.position.z;
      const sp = fetch.vel.length();
      if (sp > 1.5 && Math.hypot(dx, dz) < 0.45 && toy.y < this.height + 0.1) {
        this.lastHit = this.t;
        this.downUntil = this.t + 2.5 + Math.random() * 1.5;
        this.tipVel = 6;
        fetch.vel.x *= -0.35;
        fetch.vel.z *= -0.35;
        fetch.vel.y = Math.max(fetch.vel.y * 0.3, 1.6);
        this.onHit?.(this.name, fetch.kind, sp);
      }
    }
    // the fall and the getting up: a spring, floppy on the way down, firmer coming back
    const want = this.t < this.downUntil ? 1 : 0;
    const k = 40;
    const c = 2 * Math.sqrt(k) * (want ? 0.5 : 0.9);
    this.tipVel += (-k * (this.tip - want) - c * this.tipVel) * dt;
    this.tip += this.tipVel * dt;
    if (this.tip > 1.03) {
      this.tip = 1.03;
      this.tipVel = Math.min(0, this.tipVel);
    }
    if (this.tip < 0 && want === 0) {
      this.tip = 0;
      this.tipVel = 0;
    }

    this.cutout.update(cameraPos); // face the viewer
    this.root.rotation.x = -this.tip * 1.5; // fall away from whoever threw it
    const gait = Math.min(1.6, this.speed / 1.1); // a run bobs and leans more
    this.root.rotation.z = moving ? Math.sin(this.stride) * 0.05 * gait : 0;
    this.root.position.y = moving ? Math.abs(Math.sin(this.stride)) * 0.035 * gait : 0;
  }
}
